/**
 * Time-varying value forms of `*.sound.json` (engine/docs/systems/sound.md,
 * "Values that change over time"): pure helpers behind ParamField —
 * classification, conversion between forms, a small expression evaluator,
 * note names and envelope point editing. No React here so it is unit-tested.
 *
 * Envelope curve semantics used for drawing: the curve named on a point
 * shapes the segment that ENDS at that point (`[0.3, 0.2, "exp"]` = an
 * exponential move from the previous point to 0.2 at 0.3 s).
 */

export type EnvCurve = 'linear' | 'exp' | 'log' | 'hold' | 'smooth';
export const ENV_CURVES: readonly EnvCurve[] = ['linear', 'exp', 'log', 'hold', 'smooth'];
export type EnvPoint = [number, number] | [number, number, EnvCurve];

export type LfoShape = 'sine' | 'triangle' | 'square' | 'saw' | 'random' | 'smoothRandom';
export const LFO_SHAPES: readonly LfoShape[] = ['sine', 'triangle', 'square', 'saw', 'random', 'smoothRandom'];

export interface LfoSpec {
  shape?: LfoShape;
  rate?: number;
  depth?: number;
  center?: number;
  phase?: number;
  sync?: string;
}

export type ParamKind = 'number' | 'env' | 'adsr' | 'lfo' | 'random' | 'expr' | 'note' | 'hz' | 'add' | 'mul' | 'unknown';

export const PARAM_KIND_LABELS: Record<ParamKind, string> = {
  number: 'Number',
  env: 'Envelope',
  adsr: 'ADSR',
  lfo: 'LFO',
  random: 'Random',
  expr: 'Expression',
  note: 'Note',
  hz: 'Hz',
  add: 'Sum',
  mul: 'Product',
  unknown: 'JSON',
};

/** Kinds offered for any numeric parameter (pitch fields add `note` / `hz`). */
export const PARAM_KINDS: readonly ParamKind[] = ['number', 'env', 'adsr', 'lfo', 'random', 'expr', 'add', 'mul'];

export const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
export const isObj = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Round to 4 significant decimals so edited JSON stays readable. */
export const r4 = (v: number) => Math.round(v * 10000) / 10000;

/* ------------------------------------------------------------------ */
/* Notes                                                               */

const NOTE_RE = /^([A-Ga-g])([#b]?)(-?\d+)$/;
const NOTE_BASE: Record<string, number> = { c: 0, d: 2, e: 4, f: 5, g: 7, a: 9, b: 11 };
const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export function isNoteName(v: unknown): v is string {
  return typeof v === 'string' && NOTE_RE.test(v.trim());
}

/** `"C#4"` → 61 (60 = C4). Returns null for anything else. */
export function noteToMidi(name: string): number | null {
  const m = NOTE_RE.exec(name.trim());
  if (!m) return null;
  const base = NOTE_BASE[m[1]!.toLowerCase()]!;
  const acc = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
  return (Number(m[3]) + 1) * 12 + base + acc;
}

/** 61 → `"C#4"` (rounded to the nearest semitone). */
export function midiToNote(midi: number): string {
  const n = Math.round(midi);
  return `${SHARP_NAMES[((n % 12) + 12) % 12]}${Math.floor(n / 12) - 1}`;
}

export const mtof = (m: number) => 440 * Math.pow(2, (m - 69) / 12);
export const ftom = (f: number) => 69 + 12 * Math.log2(Math.max(1e-9, f) / 440);

/* ------------------------------------------------------------------ */
/* Expressions                                                         */

const FUNCS: Record<string, (...a: number[]) => number> = {
  min: (...a) => Math.min(...a),
  max: (...a) => Math.max(...a),
  clamp: (x = 0, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x)),
  pow: (a = 0, b = 1) => Math.pow(a, b),
  sin: (x = 0) => Math.sin(x),
  cos: (x = 0) => Math.cos(x),
  exp: (x = 0) => Math.exp(x),
  log: (x = 1) => Math.log(x),
  abs: (x = 0) => Math.abs(x),
  mtof: (x = 69) => mtof(x),
  ftom: (x = 440) => ftom(x),
  db2lin: (x = 0) => Math.pow(10, x / 20),
  lin2db: (x = 1) => 20 * Math.log10(Math.max(1e-12, x)),
};

/** Is this string a variable reference / expression (`"$pitch + 12"`)? */
export function isExpression(v: unknown): v is string {
  return typeof v === 'string' && !NOTE_RE.test(v.trim()) && v.trim() !== '';
}

/** Variable names referenced by an expression (`$name`). */
export function exprVariables(expr: string): string[] {
  return [...new Set([...expr.matchAll(/\$([A-Za-z_][A-Za-z0-9_]*)/g)].map((m) => m[1]!))];
}

/**
 * Evaluates a sound.md expression: numbers, `$vars`, `+ - * /`, unary minus,
 * parentheses and the documented functions. Throws on syntax errors or
 * unknown variables/functions.
 */
export function evalExpr(expr: string, vars: Record<string, number> = {}): number {
  const src = expr.trim();
  let i = 0;
  const ws = () => {
    while (i < src.length && /\s/.test(src[i]!)) i++;
  };
  const fail = (msg: string): never => {
    throw new Error(`${msg} at ${i + 1} in "${src}"`);
  };
  function primary(): number {
    ws();
    const c = src[i];
    if (c === undefined) return fail('Unexpected end');
    if (c === '(') {
      i++;
      const v = sum();
      ws();
      if (src[i] !== ')') fail('Expected )');
      i++;
      return v;
    }
    if (c === '-') {
      i++;
      return -primary();
    }
    if (c === '+') {
      i++;
      return primary();
    }
    if (c === '$') {
      i++;
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i));
      if (!m) return fail('Expected variable name');
      i += m[0].length;
      const v = vars[m[0]];
      if (v === undefined) fail(`Unknown variable $${m[0]}`);
      return v as number;
    }
    const num = /^(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?/.exec(src.slice(i));
    if (num) {
      i += num[0].length;
      return Number(num[0]);
    }
    const id = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i));
    if (id) {
      i += id[0].length;
      const fn = FUNCS[id[0]];
      if (!fn) return fail(`Unknown function ${id[0]}`);
      ws();
      if (src[i] !== '(') fail('Expected (');
      i++;
      const args: number[] = [];
      ws();
      if (src[i] === ')') i++;
      else {
        for (;;) {
          args.push(sum());
          ws();
          if (src[i] === ',') {
            i++;
            continue;
          }
          if (src[i] === ')') {
            i++;
            break;
          }
          fail('Expected , or )');
        }
      }
      return fn(...args);
    }
    return fail(`Unexpected "${c}"`);
  }
  function product(): number {
    let v = primary();
    for (;;) {
      ws();
      const c = src[i];
      if (c === '*') {
        i++;
        v *= primary();
      } else if (c === '/') {
        i++;
        v /= primary();
      } else return v;
    }
  }
  function sum(): number {
    let v = product();
    for (;;) {
      ws();
      const c = src[i];
      if (c === '+') {
        i++;
        v += product();
      } else if (c === '-') {
        i++;
        v -= product();
      } else return v;
    }
  }
  const v = sum();
  ws();
  if (i < src.length) fail(`Unexpected "${src[i]}"`);
  return v;
}

/** `evalExpr` that returns null instead of throwing. */
export function tryEvalExpr(expr: string, vars: Record<string, number> = {}): number | null {
  try {
    const v = evalExpr(expr, vars);
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Classification and conversion                                       */

export function isEnvPoints(v: unknown): v is EnvPoint[] {
  return (
    Array.isArray(v) &&
    v.every(
      (p) =>
        Array.isArray(p) &&
        (p.length === 2 || p.length === 3) &&
        isNum(p[0]) &&
        isNum(p[1]) &&
        (p.length === 2 || ENV_CURVES.includes(p[2] as EnvCurve)),
    )
  );
}

/** Which form a parameter value has. */
export function paramKind(v: unknown): ParamKind {
  if (v === undefined || isNum(v)) return 'number';
  if (isNoteName(v)) return 'note';
  if (isExpression(v)) return 'expr';
  if (!isObj(v)) return 'unknown';
  if ('env' in v && isEnvPoints(v.env)) return 'env';
  if ('adsr' in v && Array.isArray(v.adsr)) return 'adsr';
  if ('lfo' in v && isObj(v.lfo)) return 'lfo';
  if ('random' in v && Array.isArray(v.random) && v.random.length === 2) return 'random';
  if ('hz' in v && isNum(v.hz) && Object.keys(v).length === 1) return 'hz';
  if ('add' in v && Array.isArray(v.add)) return 'add';
  if ('mul' in v && Array.isArray(v.mul)) return 'mul';
  return 'unknown';
}

/**
 * A single representative number for any form (used when switching forms
 * and for display): the number, the envelope's first value, ADSR peak, LFO
 * center, the random range's middle, the evaluated expression, a note's
 * MIDI number (or Hz → MIDI when `pitch`).
 */
export function paramToNumber(v: unknown, vars: Record<string, number> = {}, fallback = 0): number {
  switch (paramKind(v)) {
    case 'number':
      return isNum(v) ? v : fallback;
    case 'note':
      return noteToMidi(v as string) ?? fallback;
    case 'expr':
      return tryEvalExpr(v as string, vars) ?? fallback;
    case 'hz':
      return (v as { hz: number }).hz;
    case 'env': {
      const pts = (v as { env: EnvPoint[] }).env;
      return pts[0]?.[1] ?? fallback;
    }
    case 'adsr': {
      const o = v as { peak?: unknown };
      return isNum(o.peak) ? o.peak : 1;
    }
    case 'lfo': {
      const l = (v as { lfo: LfoSpec }).lfo;
      return isNum(l.center) ? l.center : fallback;
    }
    case 'random': {
      const [a, b] = (v as { random: unknown[] }).random;
      return isNum(a) && isNum(b) ? (a + b) / 2 : fallback;
    }
    case 'add': {
      const parts = (v as { add: unknown[] }).add;
      return parts.reduce<number>((s, p) => s + paramToNumber(p, vars, 0), 0);
    }
    case 'mul': {
      const parts = (v as { mul: unknown[] }).mul;
      return parts.reduce<number>((s, p) => s * paramToNumber(p, vars, 1), 1);
    }
    default:
      return fallback;
  }
}

export interface ConvertOptions {
  vars?: Record<string, number>;
  /** Default length for new envelopes (seconds). */
  duration?: number;
  /** Fallback number when the current value is empty. */
  fallback?: number;
}

/** Converts a value to another form, keeping its representative number. */
export function convertParam(v: unknown, kind: ParamKind, opts: ConvertOptions = {}): unknown {
  if (paramKind(v) === kind) return v;
  const n = r4(v === undefined ? (opts.fallback ?? 0) : paramToNumber(v, opts.vars, opts.fallback ?? 0));
  const dur = opts.duration && opts.duration > 0 ? opts.duration : 1;
  const spread = Math.abs(n) * 0.1 || 0.1;
  switch (kind) {
    case 'number':
      return n;
    case 'env':
      return {
        env: [
          [0, n],
          [r4(dur), n],
        ],
      };
    case 'adsr':
      return { adsr: [0.01, 0.1, 0.8, 0.2], peak: n === 0 ? 1 : n, base: 0 };
    case 'lfo':
      return { lfo: { shape: 'sine', rate: 5, depth: r4(spread), center: n } };
    case 'random':
      return { random: [r4(n - spread), r4(n + spread)] };
    case 'expr': {
      const names = Object.keys(opts.vars ?? {});
      const first = names[0];
      return first !== undefined && opts.vars?.[first] === n ? `$${first}` : String(n);
    }
    case 'note':
      return midiToNote(n);
    case 'hz':
      return { hz: r4(mtof(n)) };
    case 'add':
      return { add: [n, 0] };
    case 'mul':
      return { mul: [n, 1] };
    default:
      return v;
  }
}

/* ------------------------------------------------------------------ */
/* Envelopes                                                           */

export function pointCurve(p: EnvPoint): EnvCurve {
  return p.length === 3 ? p[2] : 'linear';
}

function shape(curve: EnvCurve, f: number): number {
  switch (curve) {
    case 'exp':
      return (1 - Math.exp(-5 * f)) / (1 - Math.exp(-5));
    case 'log':
      return (Math.exp(5 * f) - 1) / (Math.exp(5) - 1);
    case 'smooth':
      return f * f * (3 - 2 * f);
    case 'hold':
      return f >= 1 ? 1 : 0;
    default:
      return f;
  }
}

/** Envelope value at time `t` (clamped outside the points; empty = 0). */
export function evalEnv(points: readonly EnvPoint[], t: number): number {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return 0;
  if (t <= first[0]) return first[1];
  if (t >= last[0]) return last[1];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    if (t <= b[0]) {
      const span = b[0] - a[0];
      const f = span > 1e-9 ? (t - a[0]) / span : 1;
      return a[1] + (b[1] - a[1]) * shape(pointCurve(b), f);
    }
  }
  return last[1];
}

const withCurve = (t: number, v: number, curve: EnvCurve): EnvPoint => (curve === 'linear' ? [t, v] : [t, v, curve]);

/** Sorted copy (stable for equal times). */
export function sortEnv(points: readonly EnvPoint[]): EnvPoint[] {
  return points
    .map((p, i) => ({ p, i }))
    .sort((a, b) => a.p[0] - b.p[0] || a.i - b.i)
    .map((x) => x.p);
}

/** Inserts a point at (t, v), returning the sorted points and the new point's index. */
export function insertEnvPoint(points: readonly EnvPoint[], t: number, v: number, curve: EnvCurve = 'linear'): { points: EnvPoint[]; index: number } {
  const p = withCurve(r4(Math.max(0, t)), r4(v), curve);
  const next = sortEnv([...points, p]);
  return { points: next, index: next.indexOf(p) };
}

/**
 * Moves point `index` to (t, v). The time is clamped between its neighbours
 * so dragging never reorders points (keeps indices stable during a drag);
 * the first point stays at its time ≥ 0.
 */
export function moveEnvPoint(points: readonly EnvPoint[], index: number, t: number, v: number): EnvPoint[] {
  const p = points[index];
  if (!p) return [...points];
  const prev = points[index - 1];
  const next = points[index + 1];
  let time = Math.max(0, t);
  if (prev) time = Math.max(prev[0], time);
  if (next) time = Math.min(next[0], time);
  const out = [...points];
  out[index] = withCurve(r4(time), r4(v), pointCurve(p));
  return out;
}

/** Removes point `index`; envelopes keep at least `minPoints` (default 2). */
export function removeEnvPoint(points: readonly EnvPoint[], index: number, minPoints = 2): EnvPoint[] {
  if (points.length <= minPoints || index < 0 || index >= points.length) return [...points];
  return points.filter((_, i) => i !== index);
}

/** Sets the curve of the segment ending at point `index` (`linear` drops the third element). */
export function setEnvCurve(points: readonly EnvPoint[], index: number, curve: EnvCurve): EnvPoint[] {
  const p = points[index];
  if (!p) return [...points];
  const out = [...points];
  out[index] = withCurve(p[0], p[1], curve);
  return out;
}

/** Time of the last point (0 for empty). */
export function envDuration(points: readonly EnvPoint[]): number {
  return points.reduce((m, p) => Math.max(m, p[0]), 0);
}

/** Multiplies every point time by `factor` (stretch/compress). */
export function scaleEnvTime(points: readonly EnvPoint[], factor: number): EnvPoint[] {
  return points.map((p) => withCurve(r4(p[0] * factor), p[1], pointCurve(p)));
}

/** Value range covering the points (and 0..1 by default), never zero-width. */
export function envRange(points: readonly EnvPoint[], min?: number, max?: number): [number, number] {
  const vals = points.map((p) => p[1]);
  let lo = min ?? Math.min(0, ...vals);
  let hi = max ?? Math.max(1, ...vals);
  if (min === undefined && vals.length) lo = Math.min(lo, ...vals);
  if (max === undefined && vals.length) hi = Math.max(hi, ...vals);
  if (hi - lo < 1e-9) hi = lo + 1;
  return [lo, hi];
}

/* ------------------------------------------------------------------ */
/* ADSR and LFO (for drawing)                                          */

/**
 * Envelope points for an ADSR `[a, d, s, r]` with peak/base, holding the
 * sustain for `hold` seconds (a drawing of one note).
 */
export function adsrToEnv(adsr: readonly number[], peak = 1, base = 0, hold = 0.3): EnvPoint[] {
  const a = Math.max(0, adsr[0] ?? 0.01);
  const d = Math.max(0, adsr[1] ?? 0.1);
  const s = adsr[2] ?? 0.8;
  const r = Math.max(0, adsr[3] ?? 0.2);
  const sus = base + (peak - base) * s;
  return [
    [0, base],
    [r4(a), peak],
    [r4(a + d), r4(sus), 'exp'],
    [r4(a + d + hold), r4(sus)],
    [r4(a + d + hold + r), base, 'exp'],
  ];
}

/** Deterministic hash noise in -1..1 for LFO random shapes. */
function hashNoise(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}

/** LFO value at time t (seconds): center + depth × shape. */
export function lfoValue(l: LfoSpec, t: number): number {
  const rate = isNum(l.rate) ? l.rate : 1;
  const depth = isNum(l.depth) ? l.depth : 0;
  const center = isNum(l.center) ? l.center : 0;
  const ph = t * rate + (isNum(l.phase) ? l.phase : 0);
  const frac = ph - Math.floor(ph);
  let w: number;
  switch (l.shape ?? 'sine') {
    case 'triangle':
      w = 1 - 4 * Math.abs(frac - 0.5);
      break;
    case 'square':
      w = frac < 0.5 ? 1 : -1;
      break;
    case 'saw':
      w = 2 * frac - 1;
      break;
    case 'random':
      w = hashNoise(Math.floor(ph));
      break;
    case 'smoothRandom': {
      const k = Math.floor(ph);
      const f = frac * frac * (3 - 2 * frac);
      w = hashNoise(k) + (hashNoise(k + 1) - hashNoise(k)) * f;
      break;
    }
    default:
      w = Math.sin(2 * Math.PI * frac);
  }
  return center + depth * w;
}
